/**
 * G2eGente - Meu DISC (App Mobile Funcionário)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeSelfAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { 
  ArrowLeft,
  Loader2,
  Brain,
  ChevronLeft,
  ChevronRight,
  Check,
  History,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

// DISC Questions
const DISC_QUESTIONS = [
  { id: 1, text: 'Gosto de assumir o controle em situações desafiadoras', disc: 'D' },
  { id: 2, text: 'Sou direto(a) e objetivo(a) ao me comunicar', disc: 'D' },
  { id: 3, text: 'Prefiro resultados rápidos a processos demorados', disc: 'D' },
  { id: 4, text: 'Tenho facilidade em tomar decisões difíceis', disc: 'D' },
  { id: 5, text: 'Fico entediado(a) facilmente com tarefas repetitivas', disc: 'D' },
  { id: 6, text: 'Gosto de interagir e conhecer novas pessoas', disc: 'I' },
  { id: 7, text: 'Sou entusiasmado(a) e otimista naturalmente', disc: 'I' },
  { id: 8, text: 'Prefiro trabalhar em equipe do que sozinho(a)', disc: 'I' },
  { id: 9, text: 'Tenho facilidade para motivar e inspirar outros', disc: 'I' },
  { id: 10, text: 'Valorizo o reconhecimento e a aprovação social', disc: 'I' },
  { id: 11, text: 'Prefiro ambientes de trabalho estáveis e previsíveis', disc: 'S' },
  { id: 12, text: 'Sou paciente e bom(boa) ouvinte', disc: 'S' },
  { id: 13, text: 'Valorizo a harmonia e evito conflitos', disc: 'S' },
  { id: 14, text: 'Sou leal e comprometido(a) com minha equipe', disc: 'S' },
  { id: 15, text: 'Prefiro mudanças graduais a transformações bruscas', disc: 'S' },
  { id: 16, text: 'Sou detalhista e perfeccionista no meu trabalho', disc: 'C' },
  { id: 17, text: 'Prefiro seguir regras e procedimentos estabelecidos', disc: 'C' },
  { id: 18, text: 'Analiso cuidadosamente antes de tomar decisões', disc: 'C' },
  { id: 19, text: 'Valorizo qualidade acima de velocidade', disc: 'C' },
  { id: 20, text: 'Sou crítico(a) e questiono informações recebidas', disc: 'C' }
];

const LIKERT = [
  { value: 1, label: 'Discordo totalmente' },
  { value: 2, label: 'Discordo' },
  { value: 3, label: 'Neutro' },
  { value: 4, label: 'Concordo' },
  { value: 5, label: 'Concordo totalmente' }
];

const DISC_COLORS = {
  D: { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-700', name: 'Dominância' },
  I: { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-700', name: 'Influência' },
  S: { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-700', name: 'Estabilidade' },
  C: { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-700', name: 'Conformidade' }
};

const DISC_DESCRIPTIONS = {
  D: 'Focado em resultados, direto e decisivo.',
  I: 'Comunicativo, entusiasmado e motivador.',
  S: 'Colaborativo, paciente e confiável.',
  C: 'Analítico, preciso e detalhista.',
  DI: 'Líder carismático que combina resultados com influência.',
  DC: 'Líder analítico que une decisão com análise.',
  ID: 'Influenciador assertivo que motiva e direciona.',
  IS: 'Comunicador empático que conecta pessoas.',
  SD: 'Executor confiável com capacidade de decisão.',
  SI: 'Apoiador que cria ambientes harmoniosos.',
  SC: 'Especialista que combina paciência com precisão.',
  CD: 'Analista decisor com precisão técnica.',
  CI: 'Comunicador técnico que explica com clareza.',
  CS: 'Perfeccionista colaborativo focado em qualidade.'
};

export default function EmployeeDISCPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [discData, setDiscData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await employeeSelfAPI.getMyDISC();
      setDiscData(res.data);
    } catch (error) {
      toast.error('Erro ao carregar perfil DISC');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (qId, value) => {
    setAnswers({ ...answers, [qId]: value });
  };

  const calculateResult = () => {
    const scores = { D: 0, I: 0, S: 0, C: 0 };
    
    DISC_QUESTIONS.forEach(q => {
      if (answers[q.id]) {
        scores[q.disc] += answers[q.id];
      }
    });
    
    const total = Object.values(scores).reduce((a, b) => a + b, 0);
    const percentages = {};
    Object.keys(scores).forEach(k => {
      percentages[k] = Math.round((scores[k] / total) * 100);
    });
    
    const sorted = Object.entries(percentages).sort((a, b) => b[1] - a[1]);
    const dominant = sorted[0][0];
    const secondary = sorted[1][0];
    const profile = percentages[secondary] >= 20 ? `${dominant}${secondary}` : dominant;
    
    return {
      disc_profile: profile,
      disc_detalhado: {
        ...percentages,
        resultado_dominante: profile,
        texto_explicativo: DISC_DESCRIPTIONS[profile] || DISC_DESCRIPTIONS[dominant]
      }
    };
  };

  const handleComplete = async () => {
    const discResult = calculateResult();
    setResult(discResult);
    
    setSaving(true);
    try {
      await employeeSelfAPI.saveMyDISC(discResult);
      toast.success('Perfil DISC salvo!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar DISC');
    } finally {
      setSaving(false);
    }
  };

  const progress = (Object.keys(answers).length / DISC_QUESTIONS.length) * 100;
  const question = DISC_QUESTIONS[currentQ];
  const canProceed = answers[question?.id];
  const isLast = currentQ === DISC_QUESTIONS.length - 1;
  const allAnswered = Object.keys(answers).length === DISC_QUESTIONS.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="employee-disc">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/m/employee')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Perfil DISC</h1>
              <p className="text-sm text-slate-500">Seu perfil comportamental</p>
            </div>
          </div>
          {discData?.history?.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => setShowHistory(!showHistory)}>
              <History className="w-4 h-4" />
            </Button>
          )}
        </div>
        {started && !result && <Progress value={progress} className="h-1" />}
      </header>

      <main className="p-4 space-y-4">
        {/* History Modal */}
        {showHistory && discData?.history?.length > 0 && (
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-sm">Histórico</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {discData.history.map((h, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                  <Badge className={`${DISC_COLORS[h.disc_profile?.[0]]?.light} ${DISC_COLORS[h.disc_profile?.[0]]?.text}`}>
                    {h.disc_profile}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {new Date(h.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Current Profile */}
        {discData?.current_profile && !started && !result && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 ${DISC_COLORS[discData.current_profile[0]]?.bg} rounded-xl flex items-center justify-center`}>
                  <span className="text-xl font-bold text-white">{discData.current_profile}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">Seu Perfil: {discData.current_profile}</h3>
                  <p className="text-sm text-slate-600">{discData.disc_detalhado?.texto_explicativo}</p>
                </div>
              </div>
              
              {/* Mini Chart */}
              <div className="grid grid-cols-4 gap-2 mt-4">
                {['D', 'I', 'S', 'C'].map(dim => (
                  <div key={dim} className="text-center">
                    <div className="h-16 bg-slate-100 rounded-lg relative overflow-hidden">
                      <div 
                        className={`absolute bottom-0 left-0 right-0 ${DISC_COLORS[dim].bg}`}
                        style={{ height: `${discData.disc_detalhado?.[dim] || 0}%` }}
                      />
                    </div>
                    <span className="text-xs font-medium text-slate-600 mt-1 block">{dim}</span>
                    <span className="text-xs text-slate-500">{discData.disc_detalhado?.[dim] || 0}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Start Button */}
        {!started && !result && (
          <Card className="border-slate-200">
            <CardContent className="py-8 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-slate-900 mb-2">
                {discData?.current_profile ? 'Refazer Teste DISC' : 'Descobrir Meu Perfil'}
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                {DISC_QUESTIONS.length} perguntas • ~5 minutos
              </p>
              <Button 
                onClick={() => { setStarted(true); setAnswers({}); setCurrentQ(0); }}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {discData?.current_profile ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refazer Teste
                  </>
                ) : (
                  <>
                    <Brain className="w-4 h-4 mr-2" />
                    Iniciar
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Questionnaire */}
        {started && !result && (
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">
                  Pergunta {currentQ + 1}/{DISC_QUESTIONS.length}
                </span>
                <Badge className={`${DISC_COLORS[question.disc].light} ${DISC_COLORS[question.disc].text}`}>
                  {DISC_COLORS[question.disc].name}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-lg text-slate-900 text-center py-4">
                "{question.text}"
              </p>
              
              <div className="space-y-2">
                {LIKERT.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(question.id, opt.value)}
                    className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                      answers[question.id] === opt.value
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm">{opt.label}</span>
                      {answers[question.id] === opt.value && (
                        <Check className="w-4 h-4 text-purple-600" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
                  disabled={currentQ === 0}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                {isLast ? (
                  <Button
                    onClick={handleComplete}
                    disabled={!allAnswered || saving}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                    Finalizar
                  </Button>
                ) : (
                  <Button
                    onClick={() => setCurrentQ(currentQ + 1)}
                    disabled={!canProceed}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {result && (
          <Card className="border-purple-200">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-cyan-50">
              <div className="flex items-center gap-3">
                <div className={`w-14 h-14 ${DISC_COLORS[result.disc_profile[0]]?.bg} rounded-xl flex items-center justify-center`}>
                  <span className="text-xl font-bold text-white">{result.disc_profile}</span>
                </div>
                <div>
                  <CardTitle>Seu Perfil: {result.disc_profile}</CardTitle>
                  <p className="text-sm text-slate-600 mt-1">
                    {result.disc_detalhado.texto_explicativo}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 gap-3">
                {['D', 'I', 'S', 'C'].map(dim => (
                  <div key={dim} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className={DISC_COLORS[dim].text}>{DISC_COLORS[dim].name}</span>
                      <span className="font-bold">{result.disc_detalhado[dim]}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${DISC_COLORS[dim].bg}`}
                        style={{ width: `${result.disc_detalhado[dim]}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              
              <Button 
                onClick={() => { setResult(null); setStarted(false); }}
                variant="outline"
                className="w-full mt-4"
              >
                Voltar
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
